import { get } from 'widgets/toolbox/util';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';
import { debounce } from 'widgets/toolbox/debounce';

/**
 * @param ShippingForm Base widget for extending
 * @returns Shipping Form widget
 */
function CoreShippingFormClassCreator(ShippingForm: ReturnType<typeof import('widgets/checkout/ShippingForm').default>) {
    class CoreShippingForm extends ShippingForm {
        loadMoreClicked = false;

        // Debounce to prevent excessive calls during autocomplete
        emitUpdateShippingMethods = debounce(() => {
            this.emit('updateshippingmethods');
        }, 100);

        prefs() {
            return {
                makeDefaultFieldId: '',
                saveAddressFieldId: '',
                failedUpdatingServerError: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.initMakeDefaultField();
        }

        /**
         * @override
         * @description Return applicable addresses for current form
         * @param order - order data
         * @param customer - customer data
         * @returns returns shipping addresses
         */
        getAddressOptions(order, customer): Array<Record<string, string>> {
            const isPickupPointShipment = !!get(order, 'shipping.0.isPickupPointShipment');
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const customerAddresses = this.prefs().enabledCountryRestriction
                ? customer.filteredAddressesByAllowedShippingCountries
                : customer.addresses;
            const allAddresses = [...customerAddresses];

            if (!isPickupPointShipment && shippingAddress) {
                allAddresses.push(shippingAddress);
            }

            return allAddresses;
        }

        /**
         * @description Select shipping method handler
         * @param inputRadio - Shipping method`s input
         * @listens InputRadio#change
         * @emits ShippingForm#updateshippingform
         * @emits "checkout.update.shipping.method"
         * @returns Promise object represents server response for shipping method selection
         */
        selectShippingMethod(inputRadio: TInputRadioInstance): ReturnType<typeof submitFormJson> {
            this.busy();

            return submitFormJson(this.prefs().selectShippingMethodUrl, {
                methodID: inputRadio.getDataValue(),
                ...this.getFormFieldsShort()
            })
                .then(response => {
                    if (response?.order) {
                        this.updateOrder(response.order);
                        this.updateState(response.order);

                        /**
                         * @description Event to update summary block
                         * @event ShippingForm#updateshippingform
                         */
                        this.emit('updateshippingform', response.order);
                        this.eventBus().emit('checkout.update.shipping.method', response.order);
                    }

                    return response;
                })
                .finally(() => {
                    this.unbusy();
                    inputRadio.ref('self').get()?.removeAttribute('aria-busy');
                });
        }

        /**
         * @description Update shipping methods list
         * @emits ShippingForm#updateshippingform
         * @returns Promise object represents server response for shipping methods update
         */
        updateShippingState() {
            // Trigger shipping methods update only when the shipping address form is valid
            if (this.isValid()) {
                this.emitUpdateShippingMethods();
            }

            return Promise.resolve(null);
        }

        /**
         * @description Submit form handler
         */
        async submitAsync(): Promise<void> {
            if (this.isValid()) {
                const response = await this.handleSubmit();

                if (response?.order) {
                    this.emitWithoutContext('updatebillingstate', response.order, response.customer);
                }

                if (!response?.success) {
                    return Promise.reject(new Error('Server response is not successful'));
                }

                if (response?.avsData?.matchedAddresses?.length && response.avsData.matchedAddresses[0].verificationStatus !== 'VALID') {
                    return Promise.reject(new Error('Adress verification failed with fix suggestions'));
                }

                return Promise.resolve(response);
            }

            this.showAddressForm();
            this.validate();

            return Promise.reject(new Error('Validation failed'));
        }

        /**
         * @description Initialize make default field
         */
        initMakeDefaultField() {
            this.getById(this.prefs().saveAddressFieldId, (saveAddressField: TInputRadioInstance) => {
                this.getById(this.prefs().makeDefaultFieldId, (makeDefaultField: TInputRadioInstance) => {
                    makeDefaultField.toggle(saveAddressField.ref('field').prop('checked'));
                });
            });
        }

        /**
         * @description Toggle make default field visibility based on save address widget state
         * @param saveAddressField - Save address widget instance
         */
        toggleMakeDefaultEv(saveAddressField: TInputRadioInstance): void {
            this.getById(this.prefs().makeDefaultFieldId, (makeDefaultField: TInputRadioInstance) => {
                makeDefaultField.toggle(saveAddressField.ref('field').prop('checked'));
                makeDefaultField.ref('field').prop('checked', false);
            });
        }

        addressLoadMore() {
            this.loadMoreClicked = true;
        }

        /**
         * @description Show errors after shipping form submission or set selected address
         * @param data - Server response object
         * @param data.cartError - Is it cart error
         * @param data.redirectUrl - Redirect url for cart Error
         * @param data.error - Is some submission errors
         * @param data.serverErrors - Common shipping step error
         * @param data.fieldErrors - Shipping form fields errors
         */
        onSubmitted(data: TServerResponse) {
            super.onSubmitted(data);

            if (!data.success && !data.serverErrors && !data.fieldErrors && !data.cartError) {
                this.setError(this.prefs().failedUpdatingServerError, true);
            }

            const form = data.form as {
                shippingAddress: {
                    addressFields: {
                        saveAddress: { selected: boolean };
                    };
                };
            };

            if (data.success) {
                this.handleGTMUpdates(data, {
                    saveAddressSelected: form.shippingAddress.addressFields.saveAddress.selected
                });
            }
        }

        /**
         * @description Handles GTM tracking updates
         * @emits account.update
         * @param response - Server response object
         * @param response.analytics - Analytics data
         * @param response.analytics.gtmUserData - User data for GTM tracking
         * @param data - Additional data for GTM updates
         * @param data.saveAddressSelected - Indicates if the save address option was selected
         */
        handleGTMUpdates(response: TServerResponse, data: { saveAddressSelected?: boolean } = {}) {
            const { analytics } = response;

            // Type assertion to inform TypeScript about the expected analytics structure
            const typedAnalytics = analytics as { gtmUserData?: object };

            if (typedAnalytics && typedAnalytics.gtmUserData) {
                this.eventBus().emit('gtm.update', { gtmUserData: typedAnalytics.gtmUserData });
            }

            if (data.saveAddressSelected) {
                this.eventBus().emit('account.update', { type: 'address_book' });
            }
        }
    }

    return CoreShippingForm;
}

export type TCoreShippingForm = ReturnType<typeof CoreShippingFormClassCreator>;

export type TCoreShippingFormInstance = InstanceType<TCoreShippingForm>;

export default CoreShippingFormClassCreator;
